import NavigationBarHeading from '@/components/Common/DesignSystem/NavigationBar/Heading/NavigationBarHeading';
import NavigationBarIconButton from '@/components/Common/DesignSystem/NavigationBar/IconButton/NavigationBarIconButton';
import NavigationBar from '@/components/Common/DesignSystem/NavigationBar/NavigationBar';
import Popup from '@/components/Common/DesignSystem/Popup/Popup';
import Share from '@/components/Common/Share';
import SunnyLayout from '@/components/Common/SunnyLayout/SunnyLayout';
import SunnyNav from '@/components/Common/SunnyNav/SunnyNav';
import { SunnyFC } from '@/components/Common/SunnyNext';
import { withSunnyPage } from '@/components/Common/SunnyPage';
import RaffleDealBody from '@/components/RaffleDeal/Body/RaffleDealBody';
import {
  RAFFLE_DEAL_LOGIN,
  RAFFLE_DEAL_LOGOUT,
  RAFFLE_DEAL_PAGE_NAME,
  RAFFLE_DEAL_PAGE_TYPE,
} from '@/components/RaffleDeal/constant';
import { IRaffleDealV3HistoryPayload } from '@/models/RaffleDeal/interfaces/IRaffleDealHistoryPayload';
import {
  IRaffleDealItem,
  IRaffleDealPromotionData,
  IRaffleDealV3PromotionsPayload,
} from '@/models/RaffleDeal/interfaces/IRaffleDealPromotionsPayload';
import { IRaffleDealV3WinHistoryPayload } from '@/models/RaffleDeal/interfaces/IRaffleDealWinHistoryPayload';
import { RaffleDealActions } from '@/models/RaffleDeal/RaffleDealActions';
import { RaffleDealRequestBuilder } from '@/models/RaffleDeal/RaffleDealRequestBuilder';
import { EN_PAGE_NAME } from '@/services/SEOService/metaData';
import { generatePageMeta } from '@/services/SEOService/metaGenerator';
import { Kinesis } from '@/services/TrackerService';
import { YaMo } from '@/services/YaMoment';
import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { RaffleDealStore, RaffleDealStoreCtx, TRaffleDealSubTab, TRaffleDealTab } from '@/stores/RaffleDealStore';
import { forceBackFromExternal, getPageUrl } from '@/utils';
import extractAPIData from '@/utils/functions/extractAPIdata';
import { getYaNative } from '@yanolja-rnd/ya-native';
import { observer } from 'mobx-react-lite';
import { Moment } from 'moment-timezone';
import qs from 'qs';
import { identity, includes, omit } from 'ramda';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';

export interface IRaffleDealProps {
  groupCode: string;
  isLoggedIn: boolean;
  activeTab: TRaffleDealTab;
  activeSubTab: TRaffleDealSubTab;
  now: Moment;
  promotionData: IRaffleDealPromotionData | 'UNAVAILABLE_PERIOD';
  isPromotionPeriod?: boolean;
  historyData: IRaffleDealV3HistoryPayload | null;
  winHistoryData: IRaffleDealV3WinHistoryPayload | null;
}

interface IRaffleDealQuery {
  [key: string]: unknown;
  group: string;
  requestedAt?: string | string[];
}

const isPromotionError = (
  data: IRaffleDealPromotionData | IRaffleDealV3PromotionsPayload | string | null,
): data is string => typeof data === 'string';

const RaffleDealPage: SunnyFC<IRaffleDealProps, IRaffleDealQuery> = (props) => {
  if (isPromotionError(props.promotionData) || !props.isPromotionPeriod) {
    const popupProps = {
      children: '프로모션 기간이 아닙니다.',
      mainBtn: {
        label: '닫기',
        onClick: () => forceBackFromExternal('/'),
      },
    };

    return <Popup isOpen={true} {...popupProps} />;
  }

  const globalStore = useContext(GlobalStoreCtx);
  const param = {
    ...props,
    promotionData: props.promotionData,
    isPromotionPeriod: props.isPromotionPeriod,
    globalStore,
  };
  const store = useMemo(() => new RaffleDealStore(param), []);

  if (!props.isLoggedIn && props.activeTab === 'HISTORY') {
    const popupProps = {
      children: '로그인이 필요합니다.',
      mainBtn: {
        label: '로그인하기',
        onClick: () =>
          store.goToLogin(
            `${window.location.pathname}?${qs.stringify({
              tab: 'HISTORY',
              subTab: 'ALL',
            })}`,
          ),
      },
      subBtn: {
        label: '닫기',
        onClick: () => forceBackFromExternal('/'),
      },
    };

    return <Popup isOpen={true} {...popupProps} />;
  }

  const onClickBack = useCallback((): void => {
    const yaNative = getYaNative();
    if (yaNative.isEnabled) {
      yaNative.dismiss();
    } else {
      history.back();
    }
  }, []);

  const url = process.browser ? `${window.location.origin}${window.location.pathname}` : '';

  const { share } = props.promotionData;
  const shareKakaoContent: Kakao.Link.DefaultFeedSettings = {
    objectType: 'feed',
    content: {
      title: share?.title ?? '',
      description: share?.content,
      /** schema 까지 정확히 들어가야 이미지가 뜹니다. */
      imageUrl: share?.imageUrl ?? '',
      imageWidth: 400,
      imageHeight: 400,
      link: {
        webUrl: url,
        mobileWebUrl: url,
      },
    },
    buttons: [
      {
        title: share?.buttonDisplayName ?? '',
        link: {
          webUrl: url,
          mobileWebUrl: url,
        },
      },
    ],
  };

  const smsMessage = `매일매일 열리는 ${store.PromotionTitle} 행운의 주인공을 찾습니다! 응모만 하세요~ 계산은 NOL이 할게요!`;

  useEffect(() => {
    Kinesis.view(RAFFLE_DEAL_PAGE_NAME, {
      pageType: RAFFLE_DEAL_PAGE_TYPE,
      object: '페이지진입',
      promotionName: `${store.PromotionTitle} 프로모션`,
      promotionDetail: store.groupCode,
      CustomerStatus: store.isLoggedIn ? RAFFLE_DEAL_LOGIN : RAFFLE_DEAL_LOGOUT,
      referrer: document.referrer,
    });
  }, []);

  return (
    <RaffleDealStoreCtx.Provider value={store}>
      <SunnyLayout>
        {{
          header: (
            <SunnyNav>
              <NavigationBar>
                {{
                  left: <NavigationBarIconButton as="button" type="icon_arrowback" onClick={onClickBack} />,
                  center: <NavigationBarHeading>{store.PromotionTitle}</NavigationBarHeading>,
                  right: share ? (
                    <NavigationBarIconButton as="button" type="icon_share" onClick={store.openShareModal} />
                  ) : null,
                }}
              </NavigationBar>
            </SunnyNav>
          ),
          body: <RaffleDealBody />,
        }}
      </SunnyLayout>

      <Share
        isOpen={store.isOpenShareModal}
        url={url}
        onRequestClose={store.closeShareModal}
        kakaoSettings={shareKakaoContent}
        smsMessage={smsMessage}
        hideFacebook
      />
    </RaffleDealStoreCtx.Provider>
  );
};

RaffleDealPage.getInitialProps = async ({ req, res, query }) => {
  const isDev = ['local', 'development', 'qa', 'stage'].includes(req.config.mode);
  const isLoggedIn = req.session.isLoggedIn();
  const tab: TRaffleDealTab = includes(query.tab, ['DRAW', 'HISTORY']) ? (query.tab as TRaffleDealTab) : 'DRAW';
  const subTab: TRaffleDealSubTab = includes(query.subTab, ['ALL', 'APPLY', 'WIN'])
    ? (query.subTab as TRaffleDealSubTab)
    : 'ALL';

  const { group, requestedAt, showUnusedYN } = query;
  const now = isDev && typeof requestedAt === 'string' ? YaMo.parseToMoment(requestedAt) : YaMo.getSeoulTime();
  const includesUnused = isDev && showUnusedYN === 'Y';

  const action = new RaffleDealActions(RaffleDealRequestBuilder, req);
  // 프로모션 정보 조회
  const promotions = await extractAPIData(
    action.getRafflePromotions({
      query: omit(isDev ? [] : ['requestAt', 'includesUnused'], {
        group,
        requestAt: now.format('YYYY-MM-DDTHH:mm:ss'),
        includesUnused,
      }),
    }),
    identity,
    (e) => e.data?.code || 'UNKNOWN_ERROR',
  );

  const headProps = generatePageMeta({
    pageUrl: getPageUrl(req),
    pageName: EN_PAGE_NAME.PAGE__PROMOTION__RAFFLE_DEAL,
    options: {
      image: ' ', // og:image 를 미노출하기 위해 space로 설정
    },
  });

  const pageCommonProps = {
    groupCode: group,
    headProps,
    isLoggedIn,
    activeTab: tab,
    activeSubTab: subTab,
    now,
    group,
  };

  if (isPromotionError(promotions) || !promotions) {
    if (promotions === 'UNAVAILABLE_PERIOD') {
      return {
        ...pageCommonProps,
        promotionData: promotions,
        historyData: null,
        winHistoryData: null,
      };
    }

    return void res.status(404);
  }

  const promotionStartAt = promotions.promotion.startAt;
  const isPromotionPeriod = now.isSameOrAfter(promotionStartAt);

  // 오늘의 NOL 드로우 상품 응모자 수 조회
  const todayDealsParticipantsCount = await Promise.all(
    promotions.runningDeals.map((deal) =>
      extractAPIData(
        action.getParticipantsCount({
          resources: {
            promotionId: String(promotions.promotion.id),
            dealId: String(deal.id),
          },
        }),
      ),
    ),
  );

  const promotionData: IRaffleDealPromotionData = {
    ...promotions,
    runningDeals: promotions.runningDeals.map((deal, idx) => ({
      ...deal,
      participantsCount: todayDealsParticipantsCount?.[idx]?.count ?? 0,
      participantsCountedAt: todayDealsParticipantsCount?.[idx]?.countedAt ?? null,
    })) as IRaffleDealItem[],
  };

  let historyData: IRaffleDealV3HistoryPayload | null = null;
  let winHistoryData: IRaffleDealV3WinHistoryPayload | null = null;
  const commonParams = {
    resources: {
      groupCode: group,
    },
    query: {
      page: 1,
      size: 10,
      requestAt: now.format('YYYY-MM-DDTHH:mm:ss'),
    },
  };

  if (isLoggedIn) {
    const historyPromise =
      subTab === 'ALL' ? action.getAllHistory(commonParams) : action.getParticipationHistory(commonParams);
    const winHistoryPromise = action.getWinHistory(commonParams);

    [historyData, winHistoryData] = await Promise.all([
      extractAPIData(historyPromise),
      extractAPIData(winHistoryPromise),
    ]);
  }

  return {
    ...pageCommonProps,
    promotionData,
    isPromotionPeriod,
    historyData,
    winHistoryData,
  };
};

export default withSunnyPage()(observer(RaffleDealPage));
